import React from 'react'
import styled from 'styled-components'

import { Badge } from 'src/components/ui/badge'
import { CloseOutlined } from 'src/icons/antd-compat'

import type { TagProps } from './props'
import { TagColorType, TagVariantType } from './types'
import {
  generateBoldHoverStyles,
  generateBoldStyles,
  generateMinimalHoverStyles,
  generateMinimalStyles,
  generateSubtleHoverStyles,
  generateSubtleStyles,
} from './utils'

/* -------------------------------------------------------------------------
 * The SHADCN half of the `src/antd/Tag` seam. `./index.tsx` is the dispatcher
 * that chooses between this and `./Tag.antd`; nothing else imports this file,
 * and nothing reaches it until someone flips the design toggle
 * (`src/design-mode`).
 *
 * Ported from the `update-design` reference branch's `src/antd/Tag/index.tsx`
 * (its Phase 2 slices 2 and 12). Divergences from the reference are marked
 * `[development]` below; everything else is the reference verbatim.
 *
 * The EXTERNAL API is the seam's own `TagProps` (`./props`), so the 322 files
 * that already import `Tag` from `src/antd` switch with no edit at all.
 *
 * WHAT THIS PORT IS NOT. The other three ported seams replace antd's rendering
 * with shadcn's. Tag's COLOURS are not antd's and never were: the seam has
 * always painted from this repo's own palette (`./utils`, `PRESET_COLORS`,
 * driven by `src/styles/raw`), and a tag's `color` is frequently free-form
 * persisted data (a waste fraction's hex from a colour picker). shadcn's token
 * set on this branch is the semantic one — primary/secondary/muted/accent/
 * destructive/border/input/ring — with no `--color-wh-*` bridge, so there is
 * no token that means "volcano 600". Inventing hexes here would be a palette
 * change smuggled in behind a mechanism change, so the variant maths stays on
 * `./utils` UNCHANGED and shared with the antd half. What actually moves is
 * the ELEMENT underneath: antd's `<span class="ant-tag">` becomes shadcn's
 * `<span data-slot="badge">`.
 *
 * `.ant-tag` is NOT re-emitted, and unlike `./Space.shadcn.tsx` (which does
 * re-emit `.ant-space`) that is forced rather than chosen: antd's `.ant-tag`
 * rules are unlayered — they outrank every Tailwind utility — and they carry
 * background, border, padding, font-size and `display`, i.e. exactly the
 * properties this seam replaces. That is the `.ant-btn` situation, not the
 * `.ant-space` one. Three live app stylesheets select `.ant-tag` on a seam tag
 * (`src/App/global.styles.ts` for `.variant-round`, and the `TagsWrapper`
 * margin reset in `src/api/helpers/{wastefraction,containertype}.tsx`); each
 * now carries a `[data-slot='badge']` selector beside its `.ant-tag` one so
 * both renderings are covered.
 *
 * [development] `Badge`'s own cva utilities that `.ant-tag` never had are kept
 * where they cannot lose content, and named here rather than fought:
 *   - `w-fit` and `justify-center` are no-ops on a shrink-to-fit inline-flex
 *     chip, so they change nothing for any tag this repo renders.
 *   - `gap-1` (4px) MATCHES antd for the `icon` PROP path — antd wraps the
 *     children in a `<span>` and spaces them with `.anticon + span
 *     { margin-left: 4px }`. It DIFFERS for an icon passed as a bare child:
 *     `<Tag><Icon /> text</Tag>` gains 4px it did not have in antd mode.
 * The two that CAN lose content — `overflow-hidden` and `shrink-0` — are
 * neutralised in `StyledTag` below.
 * ------------------------------------------------------------------------- */

const TagVariantStyleMap = {
  bold: generateBoldStyles,
  subtle: generateSubtleStyles,
  minimal: generateMinimalStyles,
}

const TagVariantHoverStyleMap = {
  bold: generateBoldHoverStyles,
  subtle: generateSubtleHoverStyles,
  minimal: generateMinimalHoverStyles,
}

/**
 * The bespoke variant colour system cannot be expressed through `Badge`'s
 * fixed variants, so it stays styled-components layered over Badge.
 * styled-components rules are UNLAYERED, so they win over Tailwind's
 * `@layer utilities` and fully own the tag's look.
 */
const StyledTag = styled(Badge)<{
  $variant: TagVariantType
  $color: TagColorType
  $withHover?: boolean
}>(
  ({ $variant, $color, $withHover }) => `
    padding: 2px 4px;
    border-radius: 4px;
    box-shadow: none;
    /* Neutralises Badge's overflow-hidden and shrink-0 utilities: antd's .ant-tag
       only sets white-space: nowrap, so a tag whose content exceeds its box
       OVERFLOWS rather than clipping, and a tag in a constrained flex row
       SHRINKS. Both only show up under a constrained parent. */
    overflow: visible;
    flex-shrink: 1;
    /* antd Tag shipped a default 8px trailing margin; keep it so tag lists
       that never set their own gap stay spaced (wastefraction resets it). */
    margin-inline-end: 8px;
    transition-duration: 0.2s;
    transition-property: background-color, color, border-color;
    font-size: 12px;
    line-height: 16px;
    font-weight: 400;
    ${TagVariantStyleMap[$variant]($color)}
    ${$withHover ? TagVariantHoverStyleMap[$variant]($color) : ''}
    /* [development] antd hides a closed tag rather than unmounting it, via
       its own .ant-tag-hidden rule; see closeTag below. */
    &[data-hidden] {
      display: none;
    }
    .wh-tag-close-icon {
      cursor: pointer;
      font-size: 10px;
      display: inline-flex;
      align-items: center;
      padding: 0;
      border: none;
      background: transparent;
      color: inherit;
      line-height: inherit;
      font-family: inherit;
    }
  `
)

/**
 * antd's `useClosable` verdict for a Tag, transcribed from
 * `antd/es/_util/hooks/useClosable.js` with Tag's own arguments
 * (`defaultClosable: false`, `defaultCloseIcon: null`).
 *
 * [development] The reference renders the close affordance on `closable`
 * alone. antd also treats a bare `closeIcon` as closable, and the repo has a
 * `closeIcon` call site (`MemberRolesModal`), so the real rule is transcribed
 * rather than approximated.
 */
const isClosable = (
  closable: TagProps['closable'],
  closeIcon: TagProps['closeIcon']
): boolean => {
  if (typeof closable === 'boolean') return closable
  if (closeIcon === undefined) return false
  return closeIcon !== false && closeIcon !== null
}

export function Tag({
  variant = 'subtle',
  color = 'gray',
  withHover = true,
  icon,
  closable,
  closeIcon,
  onClose,
  visible,
  children,
  // antd-only props with no Badge analogue — dropped so they never hit the DOM.
  bordered: _bordered,
  prefixCls: _prefixCls,
  rootClassName: _rootClassName,
  ...props
}: TagProps) {
  // [development] antd's Tag owns a `visible` state: clicking close calls
  // `onClose` and then HIDES the tag unless the handler called
  // `preventDefault()`. Live call sites depend on both halves — the contact
  // and company cockpits call `e.preventDefault()` precisely so the chip
  // survives until the refetch confirms the delete
  // (`…/standalone-contacts/view-{contact,company}/cockpit/LeftCards.tsx`) —
  // so the reference's plain `onClick={(e) => onClose?.(e)}` would silently
  // change what those screens do. Transcribed from `antd/es/tag/index.js`.
  const [selfVisible, setSelfVisible] = React.useState(true)

  React.useEffect(() => {
    if (visible !== undefined) setSelfVisible(visible)
  }, [visible])

  const closeTag = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    onClose?.(e)
    if (e.defaultPrevented) return
    setSelfVisible(false)
  }

  const showClose = isClosable(closable, closeIcon)
  const closeNode =
    closeIcon === undefined ||
    closeIcon === null ||
    typeof closeIcon === 'boolean' ? (
      <CloseOutlined />
    ) : (
      closeIcon
    )

  return (
    <StyledTag
      {...props}
      $variant={variant}
      $color={color}
      $withHover={withHover}
      data-hidden={selfVisible ? undefined : ''}
    >
      {icon}
      {children}
      {showClose && (
        <button
          type="button"
          className="wh-tag-close-icon"
          // antd's own close affordance is a `CloseOutlined` — a
          // `role="img" aria-label="close"` span with a click handler and no
          // keyboard path at all. A real button is the reference branch's
          // choice and it is kept: same untranslated antd label, plus Enter
          // and Space.
          aria-label="close"
          onClick={closeTag}
        >
          {closeNode}
        </button>
      )}
    </StyledTag>
  )
}

/**
 * `Tag.CheckableTag`, transcribed from antd `es/tag/CheckableTag.js`. antd's
 * is a plain `<span>` that toggles `.ant-tag-checkable-checked` and calls
 * `onChange(!checked)` on click; the only difference here is that the visuals
 * come from Tailwind rather than antd's cssinjs.
 *
 * Deliberately NOT the `Tag` above: antd's CheckableTag has none of the
 * variant/colour/closable surface. The seam's CheckableTag has NO live call
 * site today — the one `Tag.CheckableTag` in the repo
 * (`…/PpAddContainer/PpRouteCalendarFields.tsx`) is on raw antd, one of the 14
 * deferred raw-antd Tag files — but it is dispatched anyway, exactly as
 * `Button.Group` is, so the seam cannot end up half-switched the day a call
 * site appears.
 */
export interface CheckableTagProps {
  className?: string
  checked: boolean
  style?: React.CSSProperties
  onChange?: (checked: boolean) => void
  onClick?: (e: React.MouseEvent<HTMLElement>) => void
  children?: React.ReactNode
}

export const CheckableTag = React.forwardRef<
  HTMLSpanElement,
  CheckableTagProps
>(({ className, checked, style, onChange, onClick, children }, ref) => (
  // A bare <span> with no role, exactly as antd renders it. That is not an
  // oversight to fix here: the one call site deliberately wraps this in its
  // own focusable `role="button"` span because antd's CheckableTag never
  // accepted ARIA/DOM props, and giving this element a role too would nest
  // two interactive roles.
  // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
  <span
    ref={ref}
    data-slot="tag-checkable"
    data-checked={checked ? '' : undefined}
    className={[
      'inline-block cursor-pointer rounded-sm px-2 py-0.5 text-xs transition-colors',
      checked
        ? 'bg-primary text-primary-foreground'
        : 'bg-transparent text-foreground hover:bg-accent',
      className,
    ]
      .filter(Boolean)
      .join(' ')}
    style={style}
    onClick={(e) => {
      onChange?.(!checked)
      onClick?.(e)
    }}
  >
    {children}
  </span>
))
CheckableTag.displayName = 'Tag.CheckableTag'
